import { useEffect, useRef, useState } from 'react';
import { BrandMark } from '../components/BrandMark';
import { Link } from 'react-router-dom';
import { ThemeToggle } from '../components/ui';

const features: { title: string; description: string }[] = [
  {
    title: 'Atomic deployments',
    description:
      'Every release builds in an isolated workspace, uploads whole, and activates with a single atomic symlink swap — never a half-deployed directory serving traffic.',
  },
  {
    title: 'Real rollback safety',
    description:
      'Migration outcomes are classified from real evidence, never assumed. An unsafe schema change routes straight to recovery, not a false success.',
  },
  {
    title: 'Encrypted, verified backups',
    description:
      'Real database snapshots of your Magento store, encrypted at rest in object storage, with restore verification that actually spins up a throwaway database to prove the dump works. WordPress shops get a verified database checkpoint before every update and database-changing deployment.',
  },
  {
    title: 'Real monitoring, real alerts',
    description:
      'Smoke checks make genuine HTTPS requests on your schedule, opening and auto-resolving alerts with an optional webhook — no simulated health checks.',
  },
  {
    title: 'Owner-centric access control',
    description:
      'Invite teammates by email with configurable default or explicit roles. Every organization always keeps at least one owner.',
  },
  {
    title: 'One safety gate for live execution',
    description:
      'Nothing touches a real server until an owner explicitly, individually turns it on for that one environment — reversible any time.',
  },
];

/** LandingNav renders the real Features/Pricing links inline above 720px
 * (.landing__nav, hidden below that breakpoint by styles.css). Below
 * 720px, a compact disclosure button reveals the same links in a dropdown
 * instead of leaving them unreachable — hiding .landing__nav there used to
 * have no replacement at all. */
function LandingNav() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // The dropdown's own presence is controlled by this open state, not by
  // the 720px CSS breakpoint that hides .landing__nav-toggle — so without
  // this, opening it on a narrow viewport and then resizing (or rotating)
  // past 720px left it stuck visible with no toggle left to close it,
  // stacked next to the now-visible desktop .landing__nav. Mirrors
  // styles.css's own breakpoint exactly.
  useEffect(() => {
    const query = window.matchMedia('(min-width: 721px)');
    function onChange(event: MediaQueryListEvent) {
      if (event.matches) setOpen(false);
    }
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return (
    <div className="landing__nav-wrap" ref={rootRef}>
      <nav className="landing__nav" aria-label="Primary">
        <a href="#features">Features</a>
        <a href="#pricing">Pricing</a>
      </nav>
      <button
        type="button"
        className="landing__nav-toggle"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Toggle navigation menu"
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">☰</span>
      </button>
      {open ? (
        <nav className="landing__nav-mobile" aria-label="Primary">
          <a href="#features" onClick={() => setOpen(false)}>
            Features
          </a>
          <a href="#pricing" onClick={() => setOpen(false)}>
            Pricing
          </a>
        </nav>
      ) : null}
    </div>
  );
}

export function LandingPage() {
  return (
    <div className="landing">
      <header className="landing__header">
        <div className="landing__header-inner">
          <span className="landing__brand">
            <BrandMark size={26} />
            Commerce Ops
          </span>
          <LandingNav />
          <div className="landing__header-actions">
            <ThemeToggle />
            <Link className="button button--secondary" to="/login">
              Sign in
            </Link>
            <Link className="button" to="/login?mode=sign-up">
              Get started
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="landing__hero">
          <div className="landing__hero-inner">
            <span className="landing__eyebrow">Deployment safety for Magento and WooCommerce operators</span>
            <h1 className="landing__headline">Ship store deploys without holding your breath.</h1>
            <p className="landing__subhead">
              Commerce Ops plans, executes, and verifies atomic deployments for agencies and operators running
              Magento and WordPress with WooCommerce — real SSH execution, real rollback safety, real monitoring, with
              every dangerous action gated behind an explicit, reversible switch.
            </p>
            <div className="landing__hero-actions">
              <Link className="button button--large" to="/login?mode=sign-up">
                Start your workspace
              </Link>
              <Link className="button button--secondary button--large" to="/login">
                Sign in
              </Link>
            </div>
            <p className="landing__hero-note muted">No credit card required to start.</p>
          </div>
        </section>

        <section id="features" className="landing__features">
          <div className="landing__section-inner">
            <h2 className="landing__section-title">Built for the deploy you're actually nervous about</h2>
            <div className="landing__feature-grid">
              {features.map((feature) => (
                <div key={feature.title} className="landing__feature-card">
                  <h3>{feature.title}</h3>
                  <p className="muted">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className="landing__pricing">
          <div className="landing__section-inner landing__pricing-inner">
            <h2 className="landing__section-title">Pricing sized to your deployment volume</h2>
            <div className="landing__pricing-card">
              <span className="landing__pricing-lead">Talk to us for a plan that fits your team</span>
              <ul className="landing__pricing-list">
                <li>Real atomic deployment execution</li>
                <li>Encrypted, verified database backups</li>
                <li>Real monitoring and alerting</li>
                <li>Team invitations with role-based access</li>
              </ul>
              <Link className="button button--large" to="/login?mode=sign-up">
                Start your workspace
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="landing__footer">
        <div className="landing__footer-inner">
          <span className="muted">© {new Date().getFullYear()} Commerce Ops, a Hestaworks product.</span>
          <Link to="/login">Sign in</Link>
        </div>
      </footer>
    </div>
  );
}
